import { convertToDate } from '@fruitful/fruitils';

import { isEmpty } from '../../common/misc';
import type {
    GuideWithAvailability,
    GuideWithAvailabilityResult,
} from '../../guidance/types/Guide';
import { buildQueryParams, requestBuilder } from '../../http/RequestBuilder';

/**
 * Slots are UTC instants from Calendly (ISO strings from the API / backend), and here they are converted to Dates
 */
const normalizeAndSortGuidesWithAvailability = (
    guides: GuideWithAvailabilityResult[],
): GuideWithAvailability[] =>
    guides
        .map((guide) => ({
            ...guide,
            sessionAvailableTimes: guide.sessionAvailableTimes?.map(
                (utcTimeString) => convertToDate(utcTimeString),
            ),
        }))
        // this sort is to optimize for merging results across multiple time frame queries
        .sort((a, b) => a.firstName.localeCompare(b.firstName));

/** The Calendly API limits queries to 7 days - https://developer.calendly.com/api-docs/6a1be82aef359-list-event-type-available-times */
export const AVAILABILITY_QUERY_DAYS_LIMIT = 7;
export const AVAILABILITY_QUERY_STALE_TIME_MS = 60_000;

export interface FirstSessionAvailabilityQueryParams {
    membershipId: string;
    start: string;
    end: string;
}

const getGuidesWithFirstSessionAvailability = requestBuilder.query<
    GuideWithAvailabilityResult[],
    FirstSessionAvailabilityQueryParams,
    Error,
    GuideWithAvailability[]
>({
    endpoint: ({ membershipId, start, end }) =>
        `/api/guidance/sessions/first-session-availability${buildQueryParams({
            membershipId,
            start,
            end,
        })}`,
    queryKey: ({ membershipId, start, end }) => [
        'getGuidesWithFirstSessionAvailability',
        membershipId,
        start,
        end,
    ],
});

export const useGetGuidesWithFirstSessionAvailability = ({
    membershipId,
    start,
    end,
    enabled = true,
}: {
    membershipId: string;
    start: Date;
    end: Date;
    enabled?: boolean;
}) => {
    const query = getGuidesWithFirstSessionAvailability(
        { membershipId, start: start.toISOString(), end: end.toISOString() },
        {
            enabled: enabled && !isEmpty(membershipId),
            staleTime: AVAILABILITY_QUERY_STALE_TIME_MS,
            select: normalizeAndSortGuidesWithAvailability,
        },
    );

    return {
        guidesWithAvailability: query.data ?? null,
        isLoading: query.isLoading,
        isPending: query.isPending,
        isFetching: query.isFetching,
        isError: query.isError,
        fetchError: query.error,
        refetch: query.refetch,
        data: query.data,
    };
};
